import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, Building2, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { UserTable } from '@/components/admin/UserTable';
import { UserDetailModal } from '@/components/admin/UserDetailModal';
import {
  fetchOrganisationAssignmentsForProfiles,
  fetchUsersWithDetails,
  fetchUserStats,
  fetchUserById,
} from '@/lib/api/users';
import { fetchCompanies } from '@/lib/api/index';
import { useAuth } from '@/contexts/AuthContext';
import { useTenant } from '@/contexts/TenantContext';
import { useTenantAdmin } from '@/hooks/useTenantAdmin';
import { useQuery } from '@tanstack/react-query';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { useOrganisation } from '@/contexts/OrganisationContext';

export default function UserManagement() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const { isMasterTenant, tenant, loading: tenantLoading } = useTenant();
  const { isTenantSuperAdmin, isTenantAdmin, isBranchAdmin, isLoading: adminCheckLoading } = useTenantAdmin();
  const { hasEnterpriseHierarchy, isLoading: organisationContextLoading } = useOrganisation();
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [companyFilter, setCompanyFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(() => searchParams.get('person'));
  const pageSize = 20;

  // Use tenant from URL/TenantContext, NOT from user's profile
  // This ensures GSK subdomain shows GSK users, not the user's profile tenant
  const currentTenantId = tenant.tenant_id;

  // Visibility rules per admin tier:
  //   Tenant Super Admin / Tenant Admin → see all users in the tenant; can pick a company filter.
  //   Branch Admin (company_admin role)  → forced to their own branch only.
  // CRITICAL: use user.company_id (tenant-scoped via tenant_memberships), NOT profile.company_id
  // (profile.company_id is a single legacy column that can only hold one tenant's value).
  const userCompanyId = user?.company_id;
  const effectiveCompanyFilter = isBranchAdmin && userCompanyId
    ? userCompanyId  // Force branch admin to their own branch
    : companyFilter === 'all' ? undefined : companyFilter;
  
  // Debug log to verify correct tenant is being used

  // Fetch users with pagination (filtered by tenant from URL)
  // Branch admins: filtered by their company_id
  // Tenant super admins: can see all or filter by company
  const {
    data: usersData,
    isLoading: usersLoading,
    refetch: refetchUsers,
  } = useQuery({
    queryKey: ['users', searchQuery, roleFilter, effectiveCompanyFilter, page, currentTenantId, isBranchAdmin],
    queryFn: () =>
      fetchUsersWithDetails({
        search: searchQuery,
        role: roleFilter === 'all' ? undefined : roleFilter,
        company: effectiveCompanyFilter,
        limit: pageSize,
        offset: page * pageSize,
        tenant_id: currentTenantId,
      }),
    enabled: !tenantLoading && !adminCheckLoading, // Wait for tenant and admin check
  });

  // Fetch user stats (filtered by tenant OR company for branch admins)
  const { data: userStats, refetch: refetchUserStats } = useQuery({
    queryKey: ['user-stats', currentTenantId, effectiveCompanyFilter, isBranchAdmin],
    queryFn: () => fetchUserStats({ 
      tenant_id: currentTenantId,
      company_id: effectiveCompanyFilter 
    }),
    enabled: !tenantLoading && !adminCheckLoading,
  });

  // Fetch companies for filter (filtered by tenant from URL)
  const { data: companies } = useQuery({
    queryKey: ['companies', currentTenantId],
    queryFn: () => fetchCompanies({ tenant_id: currentTenantId }),
    enabled: !tenantLoading,
  });

  // Fetch selected user details
  const { data: selectedUser, isLoading: userDetailLoading } = useQuery({
    queryKey: ['user-detail', currentTenantId, selectedUserId],
    queryFn: () => (selectedUserId ? fetchUserById(selectedUserId, currentTenantId) : null),
    enabled: !!selectedUserId,
  });

  const organisationProfileIds = [
    ...(usersData?.users || []).map((person) => person.id),
    ...(selectedUserId ? [selectedUserId] : []),
  ];
  const {
    data: organisationAssignments = {},
    error: organisationAssignmentsError,
    isLoading: organisationAssignmentsLoading,
  } = useQuery({
    queryKey: ['user-organisation-access', currentTenantId, organisationProfileIds],
    queryFn: () => fetchOrganisationAssignmentsForProfiles(organisationProfileIds, currentTenantId),
    enabled: hasEnterpriseHierarchy && Boolean(currentTenantId) && organisationProfileIds.length > 0,
  });

  useEffect(() => {
    const requestedPerson = searchParams.get('person');
    setSelectedUserId((current) => (current === requestedPerson ? current : requestedPerson));
  }, [searchParams]);

  const openUserDetails = (userId: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('person', userId);
    setSearchParams(next, { replace: true });
    setSelectedUserId(userId);
  };

  const closeUserDetails = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('person');
    setSearchParams(next, { replace: true });
    setSelectedUserId(null);
  };

  const users = (usersData?.users || []).map((person) => ({
    ...person,
    organisation_assignments: organisationAssignmentsError
      ? undefined
      : organisationAssignments[person.id] || [],
  }));
  const selectedUserWithOrganisation = selectedUser
    ? {
        ...selectedUser,
        organisation_assignments: organisationAssignmentsError
          ? undefined
          : organisationAssignments[selectedUser.id] || [],
      }
    : null;
  const totalUsers = usersData?.total || 0;
  const totalPages = Math.ceil(totalUsers / pageSize);

  return (
    <div className="min-w-0 max-w-full space-y-6 overflow-x-hidden">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground flex items-center gap-2">
            👥 User Management
            {isBranchAdmin && (
              <Badge variant="outline" className="ml-2 text-sm font-normal">
                <Building2 className="w-3 h-3 mr-1" />
                Branch Admin
              </Badge>
            )}
            {isTenantAdmin && (
              <Badge variant="outline" className="ml-2 text-sm font-normal border-purple-300 text-purple-700">
                <Shield className="w-3 h-3 mr-1" />
                Tenant Admin
              </Badge>
            )}
            {isTenantSuperAdmin && !isMasterTenant && (
              <Badge variant="default" className="ml-2 text-sm font-normal bg-purple-600">
                <Shield className="w-3 h-3 mr-1" />
                Tenant Super Admin
              </Badge>
            )}
          </h1>
          <p className="text-muted-foreground mt-1">
            {isBranchAdmin
              ? 'Manage users in your branch'
              : 'Manage users, roles, and branch assignments'}
          </p>
        </div>
        <Button onClick={() => navigate('/admin/users/create')} className="gap-2">
          <Plus size={18} />
          Add User
        </Button>
      </div>

      {/* Stats Bar */}
      <div className="grid gap-4 md:grid-cols-3">
        <div className="bg-background border border-border rounded-xl p-6">
          <div className="text-sm text-muted-foreground">Total Users</div>
          <div className="text-3xl font-bold text-foreground mt-2">{userStats?.total || 0}</div>
          <div className="text-xs text-muted-foreground mt-1">
            Showing {users.length} of {totalUsers}
          </div>
        </div>
        <div className="bg-background border border-border rounded-xl p-6">
          <div className="text-sm text-muted-foreground">Admins</div>
          <div className="text-3xl font-bold text-green-600 mt-2">{userStats?.admins || 0}</div>
          <div className="text-xs text-muted-foreground mt-1">
            {isMasterTenant ? 'System administrators' : 'Company administrators'}
          </div>
        </div>
        <div className="bg-background border border-border rounded-xl p-6">
          <div className="text-sm text-muted-foreground">Regular Users</div>
          <div className="text-3xl font-bold text-blue-600 mt-2">{userStats?.users || 0}</div>
          <div className="text-xs text-muted-foreground mt-1">Standard accounts</div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-background border border-border rounded-xl p-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <Input
              placeholder="Search by name, email, or username..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full"
            />
          </div>
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger className="w-full md:w-[180px]">
              <SelectValue placeholder="All Roles" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Roles</SelectItem>
              <SelectItem value="admin">Tenant Admin</SelectItem>
              <SelectItem value="company_admin">Branch Admin</SelectItem>
              <SelectItem value="user">User</SelectItem>
            </SelectContent>
          </Select>
          {/* Company filter - only show for tenant super admins, not branch admins */}
          {!isBranchAdmin && (
            <Select value={companyFilter} onValueChange={setCompanyFilter}>
              <SelectTrigger className="w-full md:w-[200px]">
                <SelectValue placeholder="All Companies" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Companies</SelectItem>
                {companies?.map((company: any) => (
                    <SelectItem key={company.id} value={company.id}>
                      {company.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
          )}
          {(searchQuery || roleFilter !== 'all' || (!isBranchAdmin && companyFilter !== 'all')) && (
            <Button
              variant="outline"
              onClick={() => {
                setSearchQuery('');
                setRoleFilter('all');
                setCompanyFilter('all');
              }}
            >
              Clear Filters
            </Button>
          )}
        </div>
      </div>

      {/* Users Table */}
      {usersLoading || organisationContextLoading ? (
        <div className="bg-background border border-border rounded-xl p-12 space-y-4">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      ) : users.length === 0 ? (
        <div className="bg-background border border-border rounded-xl p-12 text-center">
          <div className="text-6xl mb-4">👥</div>
          <h3 className="text-xl font-semibold text-foreground mb-2">No users found</h3>
          <p className="text-muted-foreground mb-4">
            {searchQuery || roleFilter !== 'all' || companyFilter !== 'all'
              ? `No results for "${searchQuery}"`
              : 'Click "Add User" to create your first user!'}
          </p>
          {!searchQuery && roleFilter === 'all' && companyFilter === 'all' && (
            <Button onClick={() => navigate('/admin/users/create')}>
              <Plus size={18} className="mr-2" />
              Add User
            </Button>
          )}
        </div>
      ) : (
        <>
          <UserTable
            users={users}
            onUserClick={openUserDetails}
            onRefresh={() => Promise.all([refetchUsers(), refetchUserStats()])}
          />

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                Page {page + 1} of {totalPages}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setPage(Math.max(0, page - 1))}
                  disabled={page === 0}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
                  disabled={page >= totalPages - 1}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* User Detail Modal */}
      <UserDetailModal
        user={selectedUserWithOrganisation}
        loading={userDetailLoading || (hasEnterpriseHierarchy && organisationAssignmentsLoading)}
        open={!!selectedUserId}
        onClose={closeUserDetails}
        onEdit={() => {
          closeUserDetails();
          navigate(`/admin/users/${selectedUserId}/edit`);
        }}
      />
    </div>
  );
}
